'use client';

import { useRef } from 'react';

import { motion } from 'framer-motion';
import { Building2, FileText, ReceiptText, ThumbsUp, Users } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import { ADMIN_DEMO_SIZE, AdminShell } from './admin-shell';
import {
  AnimatedNumber,
  Avatar,
  CheckBadge,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  type DemoText,
  EASE_OUT_EXPO,
  FloatingCard,
  SPRING_SNAPPY,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Programs board for academies and schools: term programs fill as parents
 * enroll children from their family accounts, a coach takes attendance for
 * today's session, and a school's B2B contract moves from quote to invoice.
 */

export const ENROLLMENT_DEMO_SIZE = ADMIN_DEMO_SIZE;

const DURATIONS = [600, 1400, 1600, 1600, 2000, 1200, 1200, 2600] as const;

const STEP = {
  fill: 1,
  enrollLayla: 2,
  enrollYousef: 3,
  attendance: 4,
  quote: 5,
  accepted: 6,
  invoiced: 7,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of term programs for academies and schools: parents enroll children from family accounts, a coach takes attendance, and a school contract moves from quote to accepted to invoiced.',
    ar: 'عرض متحرك لبرامج الفصل للأكاديميات والمدارس: أولياء الأمور يسجلون أبناءهم من الحساب العائلي، والمدرب يسجل الحضور، وعقد مدرسة ينتقل من عرض السعر إلى الموافقة ثم الفاتورة.',
  },
  title: { en: 'Programs', ar: 'البرامج' },
  subtitle: { en: 'Autumn term', ar: 'الفصل الخريفي' },
  location: { en: 'Summit Academy', ar: 'أكاديمية سَمِت' },
  overview: { en: '3 programs · 30 places', ar: '3 برامج · 30 مقعدًا' },
  enrolled: { en: 'enrolled', ar: 'مسجّل' },
  family: { en: 'Family account', ar: 'حساب عائلي' },
  today: { en: 'Today · U10 Tennis', ar: 'اليوم · تنس تحت 10' },
  session: { en: '4:00 PM · Court 2 · Coach Rami', ar: '4:00 م · ملعب 2 · المدرب رامي' },
  absent: { en: 'Absent', ar: 'غائب' },
  presentPrefix: { en: '', ar: 'حضر' },
  presentSuffix: { en: 'present', ar: '' },
  of: { en: 'of', ar: 'من' },
  saved: { en: 'Attendance saved', ar: 'حُفظ الحضور' },
  school: { en: 'Al Noor School', ar: 'مدارس النور' },
  b2b: { en: 'B2B contract', ar: 'عقد مؤسسي' },
  contract: {
    en: '24 sessions · Coach + court · Tue & Thu 8:00 AM',
    ar: '24 حصة · مدرب وملعب · الثلاثاء والخميس 8:00 ص',
  },
  draft: { en: 'Draft', ar: 'مسودة' },
  quote: { en: 'Quote sent', ar: 'أُرسل عرض السعر' },
  accepted: { en: 'Accepted', ar: 'تمت الموافقة' },
  invoiced: { en: 'Invoice issued', ar: 'صدرت الفاتورة' },
} as const;

interface Program {
  name: DemoText;
  schedule: DemoText;
  coach: DemoText;
  coachInitials: string;
  capacity: number;
  base: number;
  /** Step at which one more child enrolls, if any. */
  enrollStep?: number;
  chip?: { child: DemoText; parent: DemoText; initials: string };
}

const PROGRAMS: Program[] = [
  {
    name: { en: 'U10 Tennis', ar: 'تنس تحت 10' },
    schedule: { en: 'Mon & Wed · 4:00 PM', ar: 'الإثنين والأربعاء · 4:00 م' },
    coach: { en: 'Coach Rami', ar: 'المدرب رامي' },
    coachInitials: 'RA',
    capacity: 12,
    base: 9,
    enrollStep: STEP.enrollLayla,
    chip: {
      child: { en: 'Layla, 9', ar: 'ليلى، 9 سنوات' },
      parent: { en: 'booked by Omar', ar: 'سجّلها عمر' },
      initials: 'LO',
    },
  },
  {
    name: { en: 'U14 Padel', ar: 'بادل تحت 14' },
    schedule: { en: 'Tue & Thu · 5:30 PM', ar: 'الثلاثاء والخميس · 5:30 م' },
    coach: { en: 'Coach Dana', ar: 'المدربة دانة' },
    coachInitials: 'DA',
    capacity: 10,
    base: 7,
    enrollStep: STEP.enrollYousef,
    chip: {
      child: { en: 'Yousef, 13', ar: 'يوسف، 13 سنة' },
      parent: { en: 'booked by Huda', ar: 'سجّلته هدى' },
      initials: 'YH',
    },
  },
  {
    name: { en: 'Adults beginners', ar: 'مبتدئون للكبار' },
    schedule: { en: 'Sat · 10:00 AM', ar: 'السبت · 10:00 ص' },
    coach: { en: 'Coach Faisal', ar: 'المدرب فيصل' },
    coachInitials: 'FA',
    capacity: 8,
    base: 5,
  },
];

const ROSTER = [
  { initials: 'LA', name: { en: 'Layla', ar: 'ليلى' }, age: { en: 'Age 9', ar: '9 سنوات' } },
  { initials: 'AD', name: { en: 'Adam', ar: 'آدم' }, age: { en: 'Age 10', ar: '10 سنوات' } },
  { initials: 'NO', name: { en: 'Noor', ar: 'نور' }, age: { en: 'Age 9', ar: '9 سنوات' } },
  { initials: 'ZA', name: { en: 'Zaid', ar: 'زيد' }, age: { en: 'Age 10', ar: '10 سنوات' } },
  { initials: 'SA', name: { en: 'Sami', ar: 'سامي' }, age: { en: 'Age 10', ar: '10 سنوات' }, absent: true },
  { initials: 'MI', name: { en: 'Mira', ar: 'ميرا' }, age: { en: 'Age 8', ar: '8 سنوات' } },
  { initials: 'TA', name: { en: 'Tala', ar: 'تالا' }, age: { en: 'Age 9', ar: '9 سنوات' } },
] as const;

const PRESENT_COUNT = ROSTER.filter((kid) => !('absent' in kid)).length;

export function EnrollmentDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS);
  const copy = localize(COPY, locale);
  const step = timeline.step;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <AdminShell
        active="calendar"
        title={copy.title}
        subtitle={copy.subtitle}
        locale={locale}
        location={copy.location}
        url="app.summitacademy.com"
      >
        <div className="flex h-full gap-3.5 p-3.5">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="h-[18px] text-[10.5px] text-[#8a857c]">{copy.overview}</p>

            {PROGRAMS.map((program) => (
              <ProgramRow key={program.name.en} program={program} step={step} locale={locale} copy={copy} />
            ))}

            <ContractCard step={step} copy={copy} rtl={locale === 'ar'} />
          </div>

          <div className="flex w-[220px] shrink-0 flex-col rounded-[14px] border border-[#ebe7e1] bg-[#f7f6f4] p-3">
            <p className="text-[12px] font-semibold text-[#202020]">{copy.today}</p>
            <p className="mt-0.5 text-[9.5px] text-[#8a857c]">{copy.session}</p>

            <div className="mt-3 flex-1 space-y-[5px]">
              {ROSTER.map((kid, index) => {
                const marked = step >= STEP.attendance;
                const delay = 0.15 + index * 0.22;
                const absent = 'absent' in kid;

                return (
                  <div
                    key={kid.initials}
                    className="flex h-[30px] items-center gap-2 rounded-[9px] bg-white px-2 ring-1 ring-[#ebe7e1]"
                  >
                    <Avatar initials={kid.initials} tone={index} size={20} className="ring-0" />
                    <span className="flex-1 text-[11px] text-[#202020]">{kid.name[locale]}</span>
                    <span className="text-[9.5px] text-[#a8a298]">{kid.age[locale]}</span>
                    <span className="relative flex h-4 w-[46px] shrink-0 items-center justify-end">
                      <motion.span
                        className="absolute end-0 top-0 h-4 w-4 rounded-full border border-[#dcd7cf]"
                        initial={false}
                        animate={{ opacity: marked ? 0 : 1 }}
                        transition={{ duration: 0.2, delay: marked ? delay : 0 }}
                      />
                      {absent ? (
                        <motion.span
                          className="absolute end-0 whitespace-nowrap rounded-full bg-[#fbf0dc] px-1.5 py-[1px] text-[8.5px] font-medium text-[#9a6412]"
                          initial={false}
                          animate={{ opacity: marked ? 1 : 0, scale: marked ? 1 : 0.6 }}
                          transition={{ ...SPRING_SNAPPY, delay: marked ? delay : 0 }}
                        >
                          {copy.absent}
                        </motion.span>
                      ) : (
                        <CheckBadge show={marked} size={16} delay={marked ? delay : 0} />
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-2.5 flex items-center justify-between border-t border-[#ebe7e1] pt-2.5 text-[10.5px]">
              <span className="flex gap-1 text-[#202020]">
                {copy.presentPrefix && <span>{copy.presentPrefix}</span>}
                <span className="font-semibold">
                  <AnimatedNumber value={step >= STEP.attendance ? PRESENT_COUNT : 0} duration={1.6} />
                </span>
                <span>
                  {copy.of} {ROSTER.length}
                </span>
                {copy.presentSuffix && <span>{copy.presentSuffix}</span>}
              </span>
              <motion.span
                className="flex items-center gap-1 text-[#2f7a63]"
                initial={false}
                animate={{ opacity: step >= STEP.attendance ? 1 : 0 }}
                transition={{ duration: 0.4, delay: step >= STEP.attendance ? 1.8 : 0 }}
              >
                <CheckBadge show={step >= STEP.attendance} size={13} delay={1.8} />
                {copy.saved}
              </motion.span>
            </div>
          </div>
        </div>
      </AdminShell>
    </DemoStage>
  );
}

function ProgramRow({
  program,
  step,
  locale,
  copy,
}: {
  program: Program;
  step: number;
  locale: DemoProps['locale'];
  copy: { [K in keyof typeof COPY]: string };
}) {
  const filled = step >= STEP.fill;
  const joined = program.enrollStep !== undefined && step >= program.enrollStep;
  const arriving = program.enrollStep === step;
  const count = filled ? program.base + (joined ? 1 : 0) : 0;

  return (
    <motion.div
      className="relative h-[64px] rounded-[12px] border bg-white px-3 py-2.5"
      initial={false}
      animate={{
        borderColor: arriving ? DEMO_COLORS.blue : DEMO_COLORS.line,
        boxShadow: arriving ? '0 0 0 3px rgba(169,200,255,0.35)' : '0 0 0 0 rgba(169,200,255,0)',
      }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-semibold text-[#202020]">{program.name[locale]}</span>
        <span className="text-[10px] text-[#8a857c]">{program.schedule[locale]}</span>
      </div>
      <div className="mt-2.5 flex items-center gap-2.5">
        <Avatar initials={program.coachInitials} tone={2} size={18} className="ring-0" />
        <span className="w-[74px] shrink-0 truncate text-[10px] text-[#8a857c]">{program.coach[locale]}</span>
        <span className="relative h-[6px] flex-1 overflow-hidden rounded-full bg-[#ede8e2]">
          <motion.span
            className="absolute inset-y-0 start-0 rounded-full bg-[#3f6099]"
            initial={false}
            animate={{ width: `${(count / program.capacity) * 100}%` }}
            transition={{ duration: filled ? 0.9 : 0.3, ease: EASE_OUT_EXPO }}
          />
        </span>
        <span className="w-[74px] shrink-0 text-end text-[10.5px] text-[#202020]">
          <span className="font-semibold">
            <AnimatedNumber value={count} duration={0.8} />
          </span>
          /{program.capacity} {copy.enrolled}
        </span>
      </div>

      {program.chip && (
        <motion.div
          className="absolute end-2.5 top-2"
          initial={false}
          animate={{ opacity: joined ? 1 : 0, y: joined ? 0 : -10, scale: joined ? 1 : 0.9 }}
          transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
        >
          <FloatingCard className="flex items-center gap-1.5 rounded-full py-[3px] pe-2.5 ps-[3px]">
            <Avatar initials={program.chip.initials} tone={0} size={18} className="ring-0" />
            <span className="whitespace-nowrap text-[10px] font-semibold text-[#202020]">
              {program.chip.child[locale]}
            </span>
            <span className="whitespace-nowrap text-[9.5px] text-[#8a857c]">
              · {program.chip.parent[locale]}
            </span>
            <span className="ms-0.5 flex items-center gap-1 whitespace-nowrap rounded-full bg-[#ddf5ec] px-1.5 py-[1px] text-[8.5px] font-medium text-[#2f7a63]">
              <Users className="h-2.5 w-2.5" />
              {copy.family}
            </span>
          </FloatingCard>
        </motion.div>
      )}
    </motion.div>
  );
}

const CONTRACT_STAGES = [
  { key: 'quote', step: STEP.quote, Icon: FileText },
  { key: 'accepted', step: STEP.accepted, Icon: ThumbsUp },
  { key: 'invoiced', step: STEP.invoiced, Icon: ReceiptText },
] as const;

function ContractCard({
  step,
  copy,
  rtl,
}: {
  step: number;
  copy: { [K in keyof typeof COPY]: string };
  rtl: boolean;
}) {
  const reached = CONTRACT_STAGES.filter((stage) => step >= stage.step);
  const current = reached[reached.length - 1];
  const status = current ? copy[current.key] : copy.draft;
  const invoiced = step >= STEP.invoiced;

  return (
    <div className="mt-auto rounded-[12px] border border-[#ebe7e1] bg-white px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#ede8e2] text-[#5c554b]">
          <Building2 className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold text-[#202020]">{copy.school}</span>
            <span className="rounded-full bg-[#ede8e2] px-1.5 py-[1px] text-[8.5px] font-medium text-[#5c554b]">
              {copy.b2b}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[10px] text-[#8a857c]">{copy.contract}</p>
        </div>
        <motion.span
          key={status}
          className={cn(
            'shrink-0 rounded-full px-2 py-[2px] text-[9.5px] font-medium',
            invoiced ? 'bg-[#ddf5ec] text-[#2f7a63]' : current ? 'bg-[#e6f0ff] text-[#2c4c80]' : 'bg-[#f3f1ed] text-[#8a857c]',
          )}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {status}
        </motion.span>
      </div>

      <div className="mt-3 flex items-start">
        {CONTRACT_STAGES.map((stage, index) => {
          const done = step >= stage.step;

          return (
            <div key={stage.key} className="flex flex-1 items-start last:flex-none">
              <div className="flex w-[84px] flex-col items-center gap-1">
                <motion.span
                  className="flex h-6 w-6 items-center justify-center rounded-full"
                  initial={false}
                  animate={{
                    backgroundColor: done ? (stage.key === 'invoiced' ? DEMO_COLORS.success : DEMO_COLORS.ink) : '#f3f1ed',
                    color: done ? '#ffffff' : '#a8a298',
                    scale: step === stage.step ? [1, 1.18, 1] : 1,
                  }}
                  transition={{ duration: 0.45 }}
                >
                  <stage.Icon className="h-3 w-3" />
                </motion.span>
                <span className={cn('whitespace-nowrap text-[9.5px]', done ? 'text-[#202020]' : 'text-[#a8a298]')}>
                  {copy[stage.key]}
                </span>
              </div>
              {index < CONTRACT_STAGES.length - 1 && (
                <span className="relative mt-3 h-[2px] flex-1 overflow-hidden rounded-full bg-[#ede8e2]">
                  <motion.span
                    className="absolute inset-0 bg-[#202020]"
                    style={{ transformOrigin: rtl ? 'right' : 'left' }}
                    initial={false}
                    animate={{ scaleX: step >= CONTRACT_STAGES[index + 1]!.step ? 1 : 0 }}
                    transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
